import { useEffect } from 'react'
import { getLang } from '../i18n/index.ts'
import { deviceClass, isAutomated, isLite, widthBucket } from './device.ts'
import { type Screen, track, trackNow } from './events.ts'
import { createLayoutShift, nameOfShifted } from './screenStats.ts'
import { currentScreen, onFirstScreen } from './screens.ts'

/**
 * One teacher's visit, as the browser sees it: what she came on, how long the app took to show its
 * first screen, and how the page felt (loading, responding, holding still).
 *
 * `visit_started` is sent once per tab (a reload of the same tab is the same visit), when the first
 * screen appears. `vitals` is sent once per page load, when the page is first hidden: those are
 * measures of a page load, and a reload is a new one. Plain `PerformanceObserver`, no library; a
 * browser that does not report a measure sends null for it, never a guess.
 */

const VISIT_KEY = 'tt-visit'
/** A page with no screen call site would never start its visit; this is how long it is given. */
const NO_SCREEN_MS = 5000

/** Set by vite.config.ts; a test or a bare script has none. */
const build = typeof __BUILD__ === 'string' ? __BUILD__ : 'dev'

type Vitals = {
	lcp: number | null
	inp: number | null
	cls: number | null
	/** What moved in the biggest single shift (screenStats.nameOfShifted). */
	shifted?: string
}

/** Starts the observers this browser supports and returns what they have measured so far. */
function observeVitals(): () => Vitals {
	let lcp: number | null = null
	let inp: number | null = null
	let worstShift = 0
	let shifted: string | undefined
	const shifts = createLayoutShift()
	const supported =
		typeof PerformanceObserver === 'undefined' ? [] : PerformanceObserver.supportedEntryTypes
	const watch = (type: string, onEntry: (e: PerformanceEntry) => void, extra: object = {}) => {
		if (!supported.includes(type)) return
		try {
			new PerformanceObserver((list) => list.getEntries().forEach(onEntry)).observe({
				type,
				buffered: true,
				...extra,
			})
		} catch {
			// A measure the browser refuses is a measure it does not report.
		}
	}
	watch('largest-contentful-paint', (e) => {
		const entry = e as PerformanceEntry & { renderTime?: number; loadTime?: number }
		lcp = Math.round(entry.renderTime || entry.loadTime || entry.startTime)
	})
	watch('layout-shift', (e) => {
		const shift = e as PerformanceEntry & {
			value: number
			hadRecentInput: boolean
			sources?: { node: Node | null }[]
		}
		if (shift.hadRecentInput) return
		shifts.add(shift.value, shift.startTime)
		if (shift.value <= worstShift) return
		worstShift = shift.value
		const node = shift.sources?.find((s) => s.node)?.node ?? null
		shifted = nameOfShifted(node instanceof Element ? node : (node?.parentElement ?? null))
	})
	// A first press is always reported, however fast: without it a visit of quick presses read as
	// "not measured" and the median counted only slow pages.
	watch('first-input', (e) => {
		inp = Math.max(inp ?? 0, Math.round(e.duration))
	})
	// The worst interaction: what INP is for fewer than fifty of them, which a teacher's visit is.
	watch(
		'event',
		(e) => {
			const ev = e as PerformanceEntry & { interactionId?: number }
			if (ev.interactionId) inp = Math.max(inp ?? 0, Math.round(ev.duration))
		},
		{ durationThreshold: 40 },
	)
	return () => ({
		lcp,
		inp,
		// Supported with nothing shifting is a real zero; unsupported is unknown.
		cls: supported.includes('layout-shift') ? shifts.score() : null,
		...(shifted && { shifted }),
	})
}

/** Whether this tab has already sent its `visit_started`. Blocked storage: every load is a visit. */
function visitedInTab(): boolean {
	try {
		return sessionStorage.getItem(VISIT_KEY) !== null
	} catch {
		return false
	}
}
function markVisited(): void {
	try {
		sessionStorage.setItem(VISIT_KEY, '1')
	} catch {
		// Nothing to remember it with.
	}
}

/**
 * Whether this browser has opened Temaly before, as a note in its own storage, written when the visit
 * is reported. False for the first visit of every browser, so for the weeks after this shipped it
 * undercounts people who came before it did.
 */
const RETURNING_KEY = 'tt-returning'
function returningBrowser(): boolean {
	try {
		const was = localStorage.getItem(RETURNING_KEY) !== null
		localStorage.setItem(RETURNING_KEY, '1')
		return was
	} catch {
		return false
	}
}

let started = false
/** Set while a screen that is almost always a student's (an editor address not hers) is showing. */
let notTeacher = false
export function notATeacherVisit(on = true): void {
	notTeacher = on
}
/**
 * The address the browser loaded. Boot time is ours only when the first screen is on it: a visit
 * that begins after an in-app move (a student page's "I made this", a not-found page's "to the
 * tools", leaving the not-yours alert) would measure how long she read, not how fast we started.
 */
const loadedPath = typeof location === 'undefined' ? '' : location.pathname
/** Idempotent: every guard that learns she has a session calls it, and the first one wins. */
export function startVisit(): void {
	if (started || typeof window === 'undefined') return
	started = true

	const read = observeVitals()
	let vitalsSent = false
	const sendVitals = () => {
		if (vitalsSent || notTeacher) return
		vitalsSent = true
		trackNow({ kind: 'vitals', data: { screen: currentScreen() ?? 'other', ...read() } })
	}
	// Marked only when sent: a page load that turned out not to be a teacher's (a student sent an
	// editor address, or the owner before she signs in) must not use up the tab's visit.
	const first = !visitedInTab()
	let said = !first
	// No screen of its own (yet) or she left first: the visit still counts, its boot time does not,
	// since it would only measure how long we waited.
	const say = (entry: Screen | 'other', now = false) => {
		if (said || notTeacher) return
		said = true
		markVisited()
		;(now ? trackNow : track)({
			kind: 'visit_started',
			data: {
				device: deviceClass(),
				width: widthBucket(),
				lite: isLite(),
				automated: isAutomated(),
				build,
				bootMs:
					entry === 'other' || location.pathname !== loadedPath
						? null
						: Math.min(Math.round(performance.now()), 3_600_000),
				entry,
				uiLang: getLang(),
				returning: returningBrowser(),
			},
		})
	}
	const leaving = () => {
		say('other', true)
		sendVitals()
	}
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'hidden') leaving()
	})
	window.addEventListener('pagehide', leaving)

	if (!first) return
	onFirstScreen(say)
	setTimeout(() => say('other'), NO_SCREEN_MS)
}

/** For the guards around the app: once she has a session, the visit has begun. */
export function useStartVisit(signedIn: boolean): void {
	useEffect(() => {
		if (signedIn) startVisit()
	}, [signedIn])
}
